/**
 * Returns modified version of the input string that's split into three lines using '\n'.
 *
 * Shorter text is split into two lines, while everything less than 20 characters is returned as is.
 */
const ensureThreeLines = (text: string): string => {
    const findIdealIndex = (sentence: string, idealIndex: number): number => {
        let actualIndex = sentence.length;

        [...sentence].forEach((char, index) => {
            if (char === ' ' && Math.abs(index - idealIndex) < Math.abs(actualIndex - idealIndex)) {
                actualIndex = index;
            }
        });

        return actualIndex;
    };

    if (text.length < 20) {
        return text;
    }

    if (text.length < 40) {
        const idealIndex = findIdealIndex(text, Math.floor(text.length / 2));

        return `${text.slice(0, idealIndex)}\n${text.slice(idealIndex)}`;
    }

    const idealFirstIndex = findIdealIndex(text, Math.floor(text.length / 3));
    const idealSecondIndex = findIdealIndex(text, 2 * Math.floor(text.length / 3));

    return `${text.slice(0, idealFirstIndex)}\n${text.slice(idealFirstIndex, idealSecondIndex)}\n${text.slice(idealSecondIndex)}`;
};

/**
 * Returns a string representing how many days/weeks/months passed since the provided date.
 *
 * Return value can be 'today' or 'X years|months|weeks|days' ago
 */
function getAgo(date: string): string {
    const msPerDay = 24 * 60 * 60 * 1000;
    const days = Math.round((new Date().getTime() - new Date(date).getTime()) / msPerDay);

    if (days === 0) return 'today';

    let unit: string;
    let value: number;

    if (days < 7) [unit, value] = ['day', days];
    else if (days < 31) [unit, value] = ['week', Math.round(days / 7)];
    else if (days < 365) [unit, value] = ['month', Math.round(days / 30.5)];
    else [unit, value] = ['year', Math.round(days / 365)];

    if (value > 1) unit = `${unit}s`;

    return `${value} ${unit} ago`;
}

/**
 * Converts seconds to a timestamp string in the format 'HH:MM:SS'.
 */
function secondsToTimestamp(timestamp: number): string {
    const values: string[] = [];

    let rest = timestamp;

    // Hours 'XX:--:--'
    if (timestamp > 60 * 60) {
        const hours = Math.floor(rest / 3600);

        values.push(hours < 10 ? `0${hours}` : hours.toString());
        rest -= hours * 3600;
    }

    // Minutes '--:XX:--'
    if (rest / 60 > 0) {
        const minutes = Math.floor(rest / 60);

        values.push(minutes < 10 ? `0${minutes}` : minutes.toString());
        rest -= minutes * 60;
    } else {
        values.push('00');
    }

    // Seconds '--:--:XX'
    if (rest > 0) {
        values.push(rest < 10 ? `0${rest}` : rest.toString());
    } else {
        values.push('00');
    }

    return values.join(':');
}

/**
 * Returns 's' if count is not 1, '' otherwise.
 */
const pluralize = (count: number): string => {
    return count === 1 ? '' : 's';
};

/**
 * Formats a date relative to now at day-level granularity:
 * 1-6 days: "X day(s) ago", 7-26 days: "X week(s) ago",
 * 26-40 days: "1 month ago", 40+ days: "Mar 12, 2025".
 *
 * Use `formatTimeRelative` instead for activity timestamps where sub-day
 * precision (minutes/hours) matters.
 */
const formatDateRelative = (dateString: string): string => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 1) return 'Today';
    if (diffDays <= 6) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    if (diffDays <= 26) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
    }
    if (diffDays <= 40) return '1 month ago';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Formats a view count compactly:
 * < 1000: full number (e.g. "400")
 * >= 1000: rounded to one decimal in k (e.g. "1.5k", "15.7k")
 */
const formatViewCount = (count: number): string => {
    if (count < 1000) return `${count}`;
    const k = count / 1000;
    return k % 1 === 0 ? `${k}k` : `${k.toFixed(1)}k`;
};

/**
 * Formats a date relative to now with sub-day precision:
 * < 1 min: "Just now", < 1 hour: "Xm ago", < 1 day: "Xh ago",
 * < 1 week: "Xd ago", older: "Mar 12" (or "Mar 12, 2024" outside current year).
 *
 * Returns '' for null/undefined/invalid input. Accepts an ISO string or a
 * millisecond timestamp.
 *
 * Use `formatDateRelative` instead when day-level granularity is enough.
 */
const formatTimeRelative = (input: string | number | null | undefined): string => {
    if (!input) return '';
    const ts = typeof input === 'number' ? input : new Date(input).getTime();
    if (Number.isNaN(ts)) return '';

    const diffMs = Date.now() - ts;
    const min = 60_000;
    const hour = 60 * min;
    const day = 24 * hour;

    if (diffMs < min) return 'Just now';
    if (diffMs < hour) return `${Math.floor(diffMs / min)}m ago`;
    if (diffMs < day) return `${Math.floor(diffMs / hour)}h ago`;
    if (diffMs < 7 * day) return `${Math.floor(diffMs / day)}d ago`;

    const date = new Date(ts);
    const currentYear = new Date().getFullYear();
    const opts: Intl.DateTimeFormatOptions =
        date.getFullYear() === currentYear
            ? { month: 'short', day: 'numeric' }
            : { month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', opts);
};

// Implementation of [FormatList](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat)
// that works with rich formats (e.g. `html<span>...</span>`), for strings, use Intl.FormatList
// Places `,` between each item except the last which gets an `and`
const listFormat = (items: Array<unknown>) => {
    if (items.length <= 1) return items;

    return items.flatMap((item, i) => {
        if (i === 0) return [item];
        const separator = items.length === 2 ? ' and ' : i === items.length - 1 ? ', and ' : ', ';
        return [separator, item];
    });
};

export {
    ensureThreeLines,
    formatDateRelative,
    formatTimeRelative,
    formatViewCount,
    getAgo,
    pluralize,
    secondsToTimestamp,
    listFormat,
};
